import { supabaseManager } from '@/services/supabaseManager';
import { formatCurrency, getCurrencySymbol as getCurrencySymbolUtil, DEFAULT_CURRENCY } from '@/lib/currency';
import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import type { Organization } from '@/types';
import type { OrganizationMembership } from '@/types';
import type { AuthStoreGet } from '@/stores/auth/auth.types';
import type { OrganizationStoreGet, OrganizationStoreSet } from './organization.types';

interface OrganizationQueryData {
  organization: Organization | null;
  membership: OrganizationMembership | null;
}

const getOrganizationQueryData = (slug: string | null): OrganizationQueryData | null => {
  if (!slug) return null;
  return queryClient.getQueryData<OrganizationQueryData>(queryKeys.organization(slug)) ?? null;
};

export const createOrganizationActions = (
  set: OrganizationStoreSet,
  get: OrganizationStoreGet,
  getAuthStore: AuthStoreGet,
) => ({
  setCurrentOrganizationSlug: (slug: string | null) => set({ currentOrganizationSlug: slug }),
  setLoading: (loading: boolean) => set({ isLoading: loading }),

  clearOrganizationState: () =>
    set({
      currentOrganizationSlug: null,
    }),

  syncOrganizationFromSlug: () => {
    const slug = supabaseManager.getCurrentOrganizationSlug();
    set({ currentOrganizationSlug: slug });
  },

  hasRole: (role: 'admin' | 'manager' | 'cashier') => {
    const authStore = getAuthStore();
    const operator = authStore.activeOperator;
    if (operator?.type === 'simple_user') {
      return (operator.role ?? 'cashier') === role;
    }

    const slug = get().currentOrganizationSlug;
    const currentMembership = getOrganizationQueryData(slug)?.membership ?? null;

    if (!currentMembership || !authStore.user) return false;
    return currentMembership.role === role;
  },

  switchOrganization: async (organizationSlug: string) => {
    try {
      set({ isLoading: true });

      await supabaseManager.connectToOrganizationBySlug(organizationSlug);
      const client = supabaseManager.getCurrentClient();

      set({ currentOrganizationSlug: organizationSlug });

      if (typeof window !== 'undefined') {
        const { notifyOrganizationChange } = await import('../authStore');
        notifyOrganizationChange();
      }

      const authStore = getAuthStore();
      if (authStore.session) {
        const { data: { session } } = await client.auth.getSession();

        if (session) {
          authStore.setSession(session);
          authStore.setUser(session.user);
        }
      }

      return true;
    } catch (error) {
      console.error('Error switching organization:', error);
      return false;
    } finally {
      set({ isLoading: false });
    }
  },

  getCurrentCurrency: () => {
    const slug = get().currentOrganizationSlug;
    const currentOrganization = getOrganizationQueryData(slug)?.organization ?? null;
    return currentOrganization?.currency ?? DEFAULT_CURRENCY;
  },

  formatAmount: (amount: number) => {
    const currency = get().getCurrentCurrency();
    return formatCurrency(amount, currency);
  },

  getCurrencySymbol: () => {
    const currency = get().getCurrentCurrency();
    return getCurrencySymbolUtil(currency);
  },
});
